import { css } from '@emotion/react';
import { useTranslation } from '@frontend/i18n';
import { Text } from '@frontend/design-system';
import { CollapsibleFilterList } from '../../../views/appointment-list-filter-button/filter-drawer/collapsible-filter-list';
import { formatAppliedFilterDate } from './helpers';
import type { FilterOptions, FilterState } from './types';

type AppliedFilterItem = {
  type: string;
  value: string;
  label: string;
};

type Props = {
  filters: FilterState;
  filterOptions: FilterOptions;
  onRemoveFilter: (type: string, value: string) => void;
};

export const AppliedFilters = ({ filters, filterOptions, onRemoveFilter }: Props) => {
  const { t } = useTranslation('schedule-activity');

  const activityTypeLabelMap = new Map(filterOptions.activityTypes.map((item) => [item.value, item.label]));
  const appointmentTypeLabelMap = new Map(filterOptions.appointmentTypes.map((item) => [item.value, item.label]));
  const sourceTypeLabelMap = new Map(filterOptions.sourceTypes.map((item) => [item.value, item.label]));
  const statusTypeLabelMap = new Map(filterOptions.statusTypes.map((item) => [item.value, item.label]));

  const appliedFilters: AppliedFilterItem[] = [
    ...filters.activityTypes.map((value) => ({
      type: 'activityTypes',
      value,
      label: activityTypeLabelMap.get(value) ?? value,
    })),
    ...filters.appointmentTypeIds.map((value) => ({
      type: 'appointmentTypeIds',
      value,
      label: appointmentTypeLabelMap.get(value) ?? value,
    })),
    ...filters.sourceTypes.map((value) => ({
      type: 'sourceTypes',
      value,
      label: sourceTypeLabelMap.get(value) ?? value,
    })),
    ...filters.statusTypes.map((value) => ({
      type: 'statusTypes',
      value,
      label: statusTypeLabelMap.get(value) ?? value,
    })),
  ];

  if (filters.startDate) {
    appliedFilters.push({
      type: 'startDate',
      value: filters.startDate,
      label: t('Start: {{date}}', { date: formatAppliedFilterDate(filters.startDate) }),
    });
  }

  if (filters.endDate) {
    appliedFilters.push({
      type: 'endDate',
      value: filters.endDate,
      label: t('End: {{date}}', { date: formatAppliedFilterDate(filters.endDate) }),
    });
  }

  if (!appliedFilters.length) return null;

  return (
    <div css={appliedFiltersContainerStyles}>
      <Text size='small' weight='bold'>
        {t('Applied Filters ({{count}})', { count: appliedFilters.length })}
      </Text>
      <CollapsibleFilterList selectedFilters={appliedFilters} onRemove={onRemoveFilter} maxRows={2} />
    </div>
  );
};

const appliedFiltersContainerStyles = css({
  display: 'flex',
  flexDirection: 'column',
  gap: 'var(--fabric-space-gap-sm)',
  padding: 'var(--fabric-space-gap-md) 0 0 0',
  borderTop: `1px solid var(--fabric-color-neutral-20)`,
  backgroundColor: 'var(--fabric-color-white)',
  zIndex: 'var(--fabric-z-index-low)',
});
